import { KeyboardEvent } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { CoverImage } from 'screens/ContentPlan/CoverImage/CoverImage'
import { ScheduleStatusPill } from 'screens/Schedule/ScheduleStatusBanner/ScheduleStatusPill'
import { ContentStrategyTypeEmoji } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { ContentStrategyTypeLabel } from '../../../../../constants'
import { CampaignOption } from 'types'
import Styles from './MoveToCampaignTable.module.scss'

interface MoveToCampaignTableProps {
  campaigns: CampaignOption[]
  selectedScheduleId: number | undefined
  onSelect: (scheduleId: number) => void
  isLoading?: boolean
}

const DATE_FORMAT = 'ddd, MMM D'

const SkeletonRow = () => (
  <tr className={Styles.tr} data-testid="move-to-campaign-skeleton-row" aria-hidden="true">
    <td className={Styles.td}>
      <LoadingShimmerer className={Styles.skeletonThumbnail} borderRadius={8} />
    </td>
    <td className={Styles.td}>
      <div className={Styles.campaignStrategy}>
        <Skeleton title={{ style: { margin: '0', borderRadius: '10px' } }} active paragraph={false} />
        <Skeleton title={{ style: { margin: '0', borderRadius: '10px', width: '70%' } }} active paragraph={false} />
      </div>
    </td>
    <td className={Styles.td}>
      <Skeleton title={{ style: { margin: '0', borderRadius: '10px' } }} active paragraph={false} />
    </td>
    <td className={Styles.td}>
      <Skeleton title={{ style: { margin: '0', borderRadius: '10px' } }} active paragraph={false} />
    </td>
  </tr>
)

const CampaignRow = ({
  campaign,
  isSelected,
  onSelect,
}: {
  campaign: CampaignOption
  isSelected: boolean
  onSelect: (id: number) => void
}) => {
  const contentStrategyType = campaign.content_strategy_type
  const title = campaign.title || 'Untitled Campaign'

  const handleSelect = () => onSelect(campaign.id)
  const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleSelect()
    }
  }

  return (
    <tr
      className={cx(Styles.tr, { [Styles.selected]: isSelected })}
      aria-selected={isSelected}
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      <td className={Styles.td}>
        <div className={Styles.coverImage}>
          {campaign.cover_image ? (
            <CoverImage src={campaign.cover_image} alt={title} width={75} height={75} />
          ) : (
            <div className={Styles.coverEmpty} />
          )}
        </div>
      </td>
      <td className={Styles.td}>
        <div className={Styles.campaignStrategy}>
          <Heading level={5} lineClamp={2}>
            {title}
          </Heading>
          {contentStrategyType && (
            <Text variant="placeholder" color="var(--dark-60)" lineClamp={1}>
              <ContentStrategyTypeEmoji contentStrategyType={contentStrategyType} color="var(--dark-100)" />
              &nbsp;{ContentStrategyTypeLabel[contentStrategyType]}
            </Text>
          )}
        </div>
      </td>
      <td className={Styles.td}>
        <Text variant="secondary" color="var(--dark-60)">
          {dayjs(campaign.start_date).format(DATE_FORMAT)} – {dayjs(campaign.end_date).format(DATE_FORMAT)}
        </Text>
      </td>
      <td className={cx(Styles.td, Styles.statusCell)}>
        <ScheduleStatusPill scheduleId={campaign.id} />
      </td>
    </tr>
  )
}

export const MoveToCampaignTable = ({
  campaigns,
  selectedScheduleId,
  onSelect,
  isLoading,
}: MoveToCampaignTableProps) => {
  const showSkeletons = isLoading && campaigns.length === 0

  return (
    <table className={Styles.table} role="grid" aria-label="Campaigns">
      <colgroup>
        <col className={Styles.column} />
        <col className={Styles.column} />
        <col className={Styles.column} />
        <col className={Styles.column} />
      </colgroup>
      <thead>
        <tr className={Styles.headerRow}>
          <th className={Styles.th} colSpan={2}>
            <Text variant="label" color="var(--dark-40)">
              Campaign
            </Text>
          </th>
          <th className={Styles.th}>
            <Text variant="label" color="var(--dark-40)">
              Timing
            </Text>
          </th>
          <th className={Styles.th}>
            <Text variant="label" color="var(--dark-40)">
              Status
            </Text>
          </th>
        </tr>
      </thead>
      <tbody>
        {showSkeletons && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}
        {!showSkeletons &&
          campaigns.map((campaign) => (
            <CampaignRow
              key={campaign.id}
              campaign={campaign}
              isSelected={campaign.id === selectedScheduleId}
              onSelect={onSelect}
            />
          ))}
      </tbody>
    </table>
  )
}
